import React from "react";
import {
  Stack,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Button,
} from "@mui/material";
import { MdSearch, MdClose, MdRefresh } from "react-icons/md";
import { T } from "theme/tokens";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    bgcolor: T.surface,
    fontSize: "0.85rem",
  },
};

/**
 * Search + date range + a set of dropdowns, in one row.
 *
 * `selects` is a list of `{ key, label, value, options: [{value, label}] }`.
 * Options come from the server wherever the underlying field has no enum, so
 * a filter can never silently exclude a value the data actually contains.
 */
export default function FilterBar({
  search,
  onSearchChange,
  searchPlaceholder = "Search…",
  from,
  to,
  onFromChange,
  onToChange,
  selects = [],
  onReset,
  onRefresh,
  children,
}) {
  const hasFilters =
    Boolean(search) ||
    Boolean(from) ||
    Boolean(to) ||
    selects.some((s) => s.value);

  return (
    <Stack
      direction={{ xs: "column", lg: "row" }}
      spacing={1.2}
      // Wrapping rather than squashing: a page with five or six filters would
      // otherwise compress every dropdown until none of the labels were
      // readable. Single-filter pages are unaffected.
      flexWrap="wrap"
      useFlexGap
      alignItems={{ xs: "stretch", lg: "center" }}
      sx={{ mb: 2 }}
    >
      {onSearchChange && (
        <TextField
          size="small"
          value={search || ""}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          sx={{ ...fieldSx, minWidth: 240, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MdSearch color={T.subtle} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange("")}>
                  <MdClose size={16} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      )}

      {onFromChange && (
        <TextField
          size="small"
          type="date"
          label="From"
          value={from || ""}
          onChange={(e) => onFromChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ ...fieldSx, minWidth: 150 }}
        />
      )}

      {onToChange && (
        <TextField
          size="small"
          type="date"
          label="To"
          value={to || ""}
          onChange={(e) => onToChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ ...fieldSx, minWidth: 150 }}
        />
      )}

      {selects.map((select) => (
        <TextField
          key={select.key}
          select
          size="small"
          label={select.label}
          value={select.value || ""}
          onChange={(e) => select.onChange(e.target.value)}
          sx={{ ...fieldSx, minWidth: select.minWidth || 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {(select.options || []).map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      ))}

      {children}

      {onRefresh && (
        <Button
          size="small"
          variant="outlined"
          onClick={onRefresh}
          startIcon={<MdRefresh />}
          sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}
        >
          Refresh
        </Button>
      )}

      {onReset && hasFilters && (
        <Button
          size="small"
          onClick={onReset}
          sx={{ textTransform: "none", color: T.muted, whiteSpace: "nowrap" }}
        >
          Clear
        </Button>
      )}
    </Stack>
  );
}
